import { createContext, useContext, useState, type ReactNode } from 'react';

// Interface para dados do side conversation (igual ao que temos na Home)
export interface SideConversationData {
  id: string;
  title: string;
  fields: string[];
  values: string[];
  createdAt: string;
  state: string;
  subject: string;
  ticket_id: number;
  products: ProductData[];
}

// Interface para dados do produto
export interface ProductData {
  id: string; // ID do incident/ticket (para deletar)
  incidentId: string; // ID do incident/ticket (backup/clareza)
  name: string;
  quantidade: string;
  preco: string;
  produtoId: string; // ID do custom object produto
  codigo?: string;
  marca?: string;
  linha?: string;
  peso?: string;
  altura?: string;
  largura?: string;
  profundidade?: string;
  ativo?: string;
  produtoComercial?: string;
  familiaProduto?: string;
  codigoErp?: string;
  imageUrl?: string;
}

interface SideConversationContextType {
  sideConversations: Record<string, SideConversationData>;
  setSideConversationData: (id: string, data: SideConversationData) => void;
  getSideConversationData: (id: string) => SideConversationData | undefined;
  removeProductFromSideConversation: (sideConversationId: string, productId: string) => void;
  clearSideConversations: () => void;
}

const SideConversationContext = createContext<SideConversationContextType | undefined>(undefined);

export function SideConversationProvider({ children }: { children: ReactNode }) {
  const [sideConversations, setSideConversations] = useState<Record<string, SideConversationData>>({});

  const setSideConversationData = (id: string, data: SideConversationData) => {
    setSideConversations(prev => ({
      ...prev,
      [id]: data
    }));
  };

  const getSideConversationData = (id: string): SideConversationData | undefined => {
    const data = sideConversations[id];
    return data;
  };

  const removeProductFromSideConversation = (sideConversationId: string, productId: string) => {
    setSideConversations(prev => {
      const current = prev[sideConversationId];
      if (!current) {
        return prev;
      }

      const updatedProducts = current.products.filter(p => p.id !== productId);
      
      return {
        ...prev,
        [sideConversationId]: {
          ...current,
          products: updatedProducts
        }
      };
    });
  };

  const clearSideConversations = () => {
    setSideConversations({});
  };

  return (
    <SideConversationContext.Provider 
      value={{ 
        sideConversations, 
        setSideConversationData, 
        getSideConversationData, 
        removeProductFromSideConversation,
        clearSideConversations 
      }}
    >
      {children}
    </SideConversationContext.Provider>
  );
}

export function useSideConversationContext() {
  const context = useContext(SideConversationContext);
  if (context === undefined) {
    throw new Error('useSideConversationContext must be used within a SideConversationProvider');
  }
  return context;
}
